// ============================================================================
// FIRM SERVICE - Multi-tenant firm management
// ============================================================================
// File: src/services/firmService.js
//
// Manages law firm profiles in Firestore. Each firm gets a unique firmId
// that is attached to ALL data (cases, clients, tasks, etc.) for isolation.
//
// New firms are created server-side by bootstrapNewFirm only (subscription
// starts as status='pending_selection'; the Welcome modal gates first login
// until a trial tier is chosen).
// ============================================================================

import { db, storage } from '../firebase-config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  doc, getDoc, updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// Trial cohort end date — all trials expire on Dec 31, 2026
export const TRIAL_END_ISO = '2026-12-31T23:59:59.999Z';

// ── Firm creation ───────────────────────────────────────────────────────────
// There is deliberately NO client-side firm creation. New firms are created
// only by the trusted bootstrapNewFirm Cloud Function (functions/
// firmBootstrapService.js: atomic firm + founder profile + teamMember,
// idempotent per account, with the accidental-duplicate confirmation), and
// firestore.rules denies direct client creates of firms/{firmId}. The former
// deprecated client createFirm function was removed (firm-creation hardening,
// 2026-10-01).

// ── Get firm by ID ──────────────────────────────────────────────────────────
export const getFirm = async (firmId) => {
  try {
    const firmSnap = await getDoc(doc(db, 'firms', firmId));
    if (firmSnap.exists()) {
      return { success: true, firm: { id: firmSnap.id, ...firmSnap.data() } };
    }
    return { success: false, error: 'Firm not found' };
  } catch (error) {
    console.error('Error getting firm:', error);
    return { success: false, error: error.message };
  }
};

// ── Update firm profile ─────────────────────────────────────────────────────
export const updateFirm = async (firmId, updates) => {
  try {
    await updateDoc(doc(db, 'firms', firmId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating firm:', error);
    return { success: false, error: error.message };
  }
};

// ── Subscription (server-owned, H1 2026-10-12) ──────────────────────────────
// firestore.rules denies every client write to firms/{firmId}.subscription.
// The free-trial tier choice (Solo / Firm / Enterprise, switchable during the
// trial) and the legacy-shape migration run in Cloud Functions; paid changes
// go through Paystack (utils/subscription.js).
export const selectTrialTier = async (firmId, tier) => {
  const fn = httpsCallable(getFunctions(), 'selectTrialTier');
  const { data } = await fn({ firmId, tier });
  return data;
};

// Migrate legacy string subscription to the structured trial object.
// Called from App.js whenever a firm loads with the old shape; the live firm
// listener picks up the result. Only a firm admin can run it (the server
// refuses anyone else). Never blocks login.
export const migrateLegacySubscription = async (firm) => {
  try {
    if (!firm || !firm.id) return firm;
    if (firm.subscription && typeof firm.subscription === 'object') return firm;
    await httpsCallable(getFunctions(), 'migrateLegacySubscription')({ firmId: firm.id });
    return firm;
  } catch (error) {
    console.error('[firmService] Legacy subscription migration failed (non-fatal):', error);
    return firm; // Never block login on migration failure
  }
};

// ── Upload firm logo ────────────────────────────────────────────────────────
export const uploadFirmLogo = async (firmId, file) => {
  try {
    const logoRef = ref(storage, `firms/${firmId}/logo`);
    await uploadBytes(logoRef, file);
    const url = await getDownloadURL(logoRef);
    await updateFirm(firmId, { logo: url });
    return { success: true, url };
  } catch (error) {
    console.error('Error uploading logo:', error);
    return { success: false, error: error.message };
  }
};

// ── Delete the firm (trusted server path, firm-deletion Phase 1) ─────────────
// The ONLY supported deletion: the deleteFirm callable logically deletes the
// firm in one transaction (tombstone + every member, seat and pending invite;
// data retained under the deletion policy) — or changes nothing. There is no
// client-side fallback: any failure, including a network error, is reported
// as a failure. Requires a sign-in within the last 5 minutes
// (reason REAUTHENTICATION_REQUIRED otherwise) and the firm's exact name.
export const deleteFirmServer = async (expectedFirmId, confirmFirmName) => {
  try {
    const fn = httpsCallable(getFunctions(), 'deleteFirm');
    const { data } = await fn({ expectedFirmId, confirmFirmName });
    return { success: true, ...data };
  } catch (error) {
    const reason = (error && error.details && error.details.reason) || null;
    return {
      success: false, reason, code: (error && error.code) || null,
      error: reason ? error.message : 'The firm could not be deleted — please check your connection and try again. Nothing was changed.',
    };
  }
};
